import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Dispatch, bindActionCreators } from 'redux';
import classNames from 'classnames';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';

import UserGlyph from '../Navbar/UserGlyph';
import mikaLogo from '../../assets/icons/mika-mes.png';
import { ReactComponent as LogOut } from '../../assets/icons/sign-out.svg';

import AdminNav from '../Admin/AdminNav';
import NotificationBell from '../NotificationBell';
import { useLocale } from '../../hooks/useLocale';
import { LOCALE_META, SupportedLocale } from '../../i18n/config';
import {
    SECTIONS,
    MODULE_ICON,
    MODULE_I18N_KEY,
    MODULE_SUB_ITEMS,
    moduleUrl,
    canSeeModule,
} from '../Navbar/navModel';
import './MobileNav.scss';

/**
 * Mobile navigation — the spec's "one control, one job" model:
 *
 *   • Fixed top app bar (always visible): ☰ hamburger · Mika logo · 🔔 bell ·
 *     avatar. The bar is the only persistent chrome; nothing else stays on
 *     screen.
 *   • Off-canvas drawer (from ☰): navigation ONLY — user header, the module
 *     groups for the current context, and a single context-switch button in
 *     the footer. No notifications, no language, no logout here.
 *   • Account menu (from the avatar): a small popover with Gjuha (language) and
 *     Dil (logout). Notifications never appear here — the bell owns them.
 *
 * Replaces the previous always-visible icon rail (MobileNavRail). Rendered only
 * at <576px (Dashboard.tsx gates via useIsMobile); desktop uses NavbarV2.
 *
 * Nav data (sections, icons, labels, URLs, permission gates, sub-items) comes
 * from the shared ../Navbar/navModel so this surface can't drift from desktop.
 */

const Icon = {
    menu: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
    ),
    x: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6 6 18" />
        </svg>
    ),
    chevDown: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
        </svg>
    ),
    chevRight: (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 6 15 12 9 18" />
        </svg>
    ),
    globe: (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" />
        </svg>
    ),
    swap: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 4 3 8l4 4M3 8h12M17 20l4-4-4-4M21 16H9" />
        </svg>
    ),
};

interface Props {
    intl: any;
    history: any;
    location: any;
    permissions: any;
    isAdminPanelOpen: boolean;
    companyProfile?: { kubitEnabled?: boolean; enabledModules?: string[] } | null;
    user: any;
    actions: any;
}

const MobileNav: React.FC<Props> = (props) => {
    const { intl, history, location, permissions, companyProfile, user } = props;

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [accountOpen, setAccountOpen] = useState(false);
    const [showLangs, setShowLangs] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

    // Pre-expand the submenu matching the current route so the user sees where
    // they are when opening the drawer on a sub-page.
    const [expandedModules, setExpandedModules] = useState<Set<string>>(() => {
        const path = location?.pathname || '';
        const match = Object.keys(MODULE_SUB_ITEMS).find(k => path.startsWith(`/dashboard/${k}`));
        return match ? new Set([match]) : new Set();
    });

    const { locale, setLocale, activeLocales } = useLocale();

    const currentPath = location?.pathname || '';
    const isAdminContext = currentPath.startsWith('/admin');

    // Opening the drawer closes the account menu (and vice versa) — only one
    // overlay at a time, per the spec.
    const openDrawer = useCallback(() => { setDrawerOpen(true); setAccountOpen(false); setShowLangs(false); }, []);
    const closeDrawer = useCallback(() => setDrawerOpen(false), []);
    const toggleAccount = useCallback(() => { setAccountOpen(o => !o); setDrawerOpen(false); }, []);
    const closeAccount = useCallback(() => { setAccountOpen(false); setShowLangs(false); }, []);

    const toggleGroup = useCallback((id: string) => {
        setCollapsedGroups(c => ({ ...c, [id]: !c[id] }));
    }, []);

    const toggleModule = useCallback((moduleKey: string) => {
        setExpandedModules(prev => {
            const next = new Set(prev);
            if (next.has(moduleKey)) next.delete(moduleKey); else next.add(moduleKey);
            return next;
        });
    }, []);

    // Esc closes whatever overlay is open.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { setDrawerOpen(false); setAccountOpen(false); setShowLangs(false); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // Lock background scroll while the full-height drawer is open.
    useEffect(() => {
        if (!drawerOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [drawerOpen]);

    const navigateAndClose = useCallback((path: string) => {
        setDrawerOpen(false);
        setAccountOpen(false);
        history.push(path);
    }, [history]);

    const logOut = useCallback(() => {
        try {
            localStorage.removeItem('mikaBearerToken');
            localStorage.removeItem('username');
            localStorage.removeItem('mikaUserId');
            localStorage.removeItem('userPermissions');
        } catch { /* swallow — storage errors must not block the redirect */ }
        // Hard navigation — avoids races with in-flight axios calls during
        // route teardown (same rationale as the legacy Header/rail logout).
        window.location.assign('/login');
    }, []);

    const username = user?.username || localStorage.getItem('username') || '';

    const roleLabel = permissions?.superAdmin
        ? <FormattedMessage id="admin.role.superAdmin" defaultMessage="Super admin" />
        : permissions?.fullPrivileges
            ? <FormattedMessage id="admin.role.admin" defaultMessage="Admin" />
            : <FormattedMessage id="admin.role.user" defaultMessage="User" />;

    const isModuleActive = (moduleKey: string) => currentPath.startsWith(`/dashboard/${moduleKey}`);

    // Visible sections for the Sales context (admin context renders <AdminNav/>).
    const visibleSections = useMemo(
        () => SECTIONS
            .map(s => ({ ...s, modules: s.modules.filter(m => canSeeModule(m, permissions, companyProfile)) }))
            .filter(s => s.modules.length > 0),
        [permissions, companyProfile],
    );

    const renderModuleRow = (moduleKey: string) => {
        const label = intl.formatMessage({ id: MODULE_I18N_KEY[moduleKey], defaultMessage: moduleKey });
        const icon = MODULE_ICON[moduleKey];
        const active = isModuleActive(moduleKey);
        const subs = (MODULE_SUB_ITEMS[moduleKey] || []).filter(s => !s.canSee || s.canSee(permissions));

        // No sub-pages → a plain link that navigates and closes the drawer.
        if (subs.length === 0) {
            return (
                <Link
                    key={moduleKey}
                    to={moduleUrl(moduleKey)}
                    className={classNames('mnav-item', { 'is-active': active })}
                    onClick={closeDrawer}
                    title={label}
                >
                    <span className="mnav-item-icon"><img src={icon} alt="" /></span>
                    <span className="mnav-item-label">
                        <FormattedMessage id={MODULE_I18N_KEY[moduleKey]} defaultMessage={moduleKey} />
                    </span>
                </Link>
            );
        }

        // Has sub-pages → an expandable row that toggles an inline sub-list.
        const expanded = expandedModules.has(moduleKey);
        return (
            <div key={moduleKey} className="mnav-item-group">
                <button
                    type="button"
                    className={classNames('mnav-item', 'mnav-item--expandable', { 'is-active': active, 'is-expanded': expanded })}
                    onClick={() => toggleModule(moduleKey)}
                    aria-expanded={expanded}
                    title={label}
                >
                    <span className="mnav-item-icon"><img src={icon} alt="" /></span>
                    <span className="mnav-item-label">
                        <FormattedMessage id={MODULE_I18N_KEY[moduleKey]} defaultMessage={moduleKey} />
                    </span>
                    <span className="mnav-item-caret" aria-hidden="true">{Icon.chevRight}</span>
                </button>
                {expanded && (
                    <div className="mnav-subitems">
                        {subs.map(sub => (
                            <Link
                                key={sub.path}
                                to={sub.path}
                                className={classNames('mnav-subitem', { 'is-active': currentPath === sub.path })}
                                onClick={closeDrawer}
                            >
                                <FormattedMessage id={sub.labelKey} defaultMessage={sub.defaultLabel} />
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const langOptions = (activeLocales && activeLocales.length ? activeLocales : Object.keys(LOCALE_META)) as SupportedLocale[];
    const showLangControl = langOptions.length > 1;
    const currentMeta = LOCALE_META[locale];

    // Footer context-switch button (only when there's somewhere to switch to).
    const contextButton = isAdminContext ? (
        <button
            type="button"
            className="mnav-foot-btn"
            onClick={() => { props.actions.openAdminPanel(false); navigateAndClose('/dashboard/contact'); }}
        >
            {Icon.swap}<span><FormattedMessage id="nav.user.backToSystem" defaultMessage="Back to system" /></span>
        </button>
    ) : permissions?.fullPrivileges ? (
        <button
            type="button"
            className="mnav-foot-btn"
            onClick={() => { props.actions.openAdminPanel(true); navigateAndClose('/admin'); }}
        >
            {Icon.swap}<span><FormattedMessage id="user.menu.settings" defaultMessage="Settings" /></span>
        </button>
    ) : null;

    return (
        <>
            {/* ── Top app bar (always visible) ───────────────────────────── */}
            <header className="mtb">
                <button
                    type="button"
                    className="mtb__burger"
                    onClick={openDrawer}
                    aria-label={intl.formatMessage({ id: 'nav.menu.open', defaultMessage: 'Open menu' })}
                    aria-expanded={drawerOpen}
                >
                    {Icon.menu}
                </button>

                <div className="mtb__logo">
                    <img src={mikaLogo} alt="Mika" />
                </div>

                <div className="mtb__right">
                    <NotificationBell />
                    <button
                        type="button"
                        className={classNames('mtb__avatar', { 'is-on': accountOpen })}
                        onClick={toggleAccount}
                        aria-haspopup="menu"
                        aria-expanded={accountOpen}
                        aria-label={intl.formatMessage({ id: 'nav.account.open', defaultMessage: 'Account' })}
                    >
                        <UserGlyph />
                    </button>
                </div>
            </header>

            {/* ── Account menu (from avatar) ─────────────────────────────── */}
            <div
                className={classNames('mnav-acct-scrim', { 'is-open': accountOpen })}
                onClick={closeAccount}
                aria-hidden="true"
            />
            <div className={classNames('mnav-acct', { 'is-open': accountOpen })} role="menu">
                <div className="mnav-acct-head">
                    <span className="mnav-ava mnav-ava--sm"><UserGlyph /></span>
                    <div className="mnav-acct-head-meta">
                        <div className="nm">{username}</div>
                        <div className="rl">{roleLabel}</div>
                    </div>
                </div>

                {showLangControl && (
                    <>
                        <button
                            type="button"
                            className="mnav-acct-item"
                            onClick={() => setShowLangs(v => !v)}
                            aria-expanded={showLangs}
                        >
                            {Icon.globe}
                            <span><FormattedMessage id="user.menu.language" defaultMessage="Language" /></span>
                            <b>{currentMeta?.native ?? locale}</b>
                        </button>
                        {showLangs && (
                            <ul className="mnav-acct-langs">
                                {langOptions.map(code => (
                                    <li
                                        key={code}
                                        className={classNames('mnav-acct-lang', { 'is-active': code === locale })}
                                        onClick={() => { setShowLangs(false); if (code !== locale) setLocale(code); }}
                                    >
                                        <span className="native">{LOCALE_META[code]?.native ?? code}</span>
                                        <span className="code">{code}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="mnav-acct-sep" />
                    </>
                )}

                <button
                    type="button"
                    className="mnav-acct-item mnav-acct-item--danger"
                    onClick={() => { setAccountOpen(false); setShowLogoutConfirm(true); }}
                >
                    <LogOut style={{ width: 17, height: 17 }} />
                    <span><FormattedMessage id="user.menu.logout" defaultMessage="Log out" /></span>
                </button>
            </div>

            {/* ── Navigation drawer (from hamburger) ─────────────────────── */}
            <div
                className={classNames('mnav-scrim', { 'is-open': drawerOpen })}
                onClick={closeDrawer}
                aria-hidden="true"
            />
            <aside
                className={classNames('mnav-drawer', { 'is-open': drawerOpen })}
                role="navigation"
                aria-label={intl.formatMessage({ id: 'nav.menu.label', defaultMessage: 'Primary navigation' })}
                aria-hidden={!drawerOpen}
            >
                <div className="mnav-user">
                    <span className="mnav-ava"><UserGlyph /></span>
                    <div className="mnav-user-meta">
                        <div className="nm">{username}</div>
                        <div className="rl">{roleLabel}</div>
                    </div>
                    <button
                        type="button"
                        className="mnav-user-x"
                        onClick={closeDrawer}
                        aria-label={intl.formatMessage({ id: 'nav.menu.close', defaultMessage: 'Close menu' })}
                    >
                        {Icon.x}
                    </button>
                </div>

                <div className="mnav-body">
                    {isAdminContext ? (
                        <AdminNav currentPath={currentPath} onLinkClick={closeDrawer} />
                    ) : (
                        visibleSections.map(section => {
                            const groupCollapsed = !!collapsedGroups[section.id];
                            return (
                                <div key={section.id} className="mnav-group">
                                    <button
                                        type="button"
                                        className={classNames('mnav-group-label', { 'is-collapsed': groupCollapsed })}
                                        onClick={() => toggleGroup(section.id)}
                                        aria-expanded={!groupCollapsed}
                                    >
                                        <span>
                                            <FormattedMessage id={section.labelId} defaultMessage={section.defaultLabel} />
                                        </span>
                                        <span className="mnav-group-chev" aria-hidden="true">{Icon.chevDown}</span>
                                    </button>
                                    {!groupCollapsed && (
                                        <div className="mnav-group-items">
                                            {section.modules.map(m => renderModuleRow(m))}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {contextButton && (
                    <div className="mnav-foot">
                        {contextButton}
                    </div>
                )}
            </aside>

            {/* ── Logout confirm ─────────────────────────────────────────── */}
            {showLogoutConfirm && (
                <div className="mnav-logout-overlay" onClick={() => setShowLogoutConfirm(false)}>
                    <div className="mnav-logout-modal" onClick={e => e.stopPropagation()}>
                        <LogOut style={{ width: 24, height: 24, color: '#ef4444' }} />
                        <h3><FormattedMessage id="user.menu.logoutConfirmTitle" defaultMessage="Log out?" /></h3>
                        <p><FormattedMessage id="user.menu.logoutConfirmMessage" defaultMessage="Your session will end and you'll be redirected to the login page." /></p>
                        <div className="mnav-logout-actions">
                            <button type="button" onClick={() => setShowLogoutConfirm(false)}>
                                <FormattedMessage id="common.confirm.cancel" defaultMessage="Cancel" />
                            </button>
                            <button type="button" className="mnav-logout-confirm" onClick={logOut}>
                                <FormattedMessage id="user.menu.logoutConfirmYes" defaultMessage="Yes, log out" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        user: state.user,
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(MobileNav as any)));
